import type {
    AppRuntimeData,
    RuntimeComponent,
    EditorReactComponent,
    ComponentData,
    EditorContextProvider,
    EditorFunctionLibrary
} from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types'

export interface TransformedRuntimeComponent extends Omit<RuntimeComponent, 'data'> {
    data?: EditorReactComponent | EditorContextProvider | EditorFunctionLibrary
}

export interface TransformedAppRuntimeData extends Omit<AppRuntimeData, 'components'> {
    components?: TransformedRuntimeComponent[]
}

export interface AppsRuntimeDataServer {
    getComponentsByAppIds(
        appDefinitionIds: {appDefinitionId: string; version?: string}[],
        overrideIds?: string[],
        languageCode?: string
    ): Promise<TransformedAppRuntimeData[]>
}

export function mapGetComponentsResponse(appsRuntimeData: AppRuntimeData[]): TransformedAppRuntimeData[] {
    return appsRuntimeData.map((appData: AppRuntimeData) => {
        const transformedAppData: TransformedAppRuntimeData = {
            ...appData,
            components: appData.components?.map(mapComponentDataByType)
        }
        return transformedAppData
    })
}

// Function to convert upper snake case to lower camel case that is memoized
const upperSnakeCaseToLowerCamelCase = (() => {
    const computedKeys: Record<string, string> = {}

    return (str: string) => {
        if (computedKeys[str]) {
            return computedKeys[str]
        }
        const camelCaseKey = str
            .toLowerCase()
            .split('_')
            .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
            .join('')

        computedKeys[str] = camelCaseKey
        return camelCaseKey
    }
})()

function mapComponentDataByType(component: RuntimeComponent): TransformedRuntimeComponent {
    const componentDataKey = upperSnakeCaseToLowerCamelCase(component.type ?? '')
    return {
        ...component,
        data: component.data?.[componentDataKey as keyof ComponentData] as
            | EditorReactComponent
            | EditorContextProvider
            | EditorFunctionLibrary
            | undefined
    }
}
